import { createApiClient } from "@journal/client";
import {
  createMutation,
  createQuery,
  type MutationOptions,
  type QueryKey,
  type QueryOptions,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/solid-query";
import {
  buildStorageUrl,
  handleApiResponse,
  recordAuthTransportFailure,
  resolveServerUrl,
} from "@journal/shared";
// Importing this module registers this build's version/hash/platform with the
// shared telemetry state — see the file header for why initialisation is a side
// effect of the import rather than a call someone has to remember.
import { buildClientInfoHeader } from "@/lib/client-telemetry";
import { readActiveAccountSelector } from "@/lib/accounts";
import { isTauriRuntime } from "@/lib/platform";
import {
  hasTokenPresence,
  LEGACY_TOKEN_ACCOUNT,
  readLegacyToken,
  SESSION_TOKEN_STORAGE_KEY,
  writeLegacyToken,
} from "@/lib/secure-token";
import { createNativeApiFetch } from "@/lib/native-transport";
import { SERVER_URL } from "@/lib/runtime-config";
import { OBJECT_URL_REVOKE_DELAY_MS } from "@/lib/constants";

/**
 * Key historically used to persist the legacy (userId-less) session bearer
 * token. Re-exported from `secure-token.ts` for the PowerSync Tauri connector.
 *
 * **Only used inside the Tauri WebView** (desktop + mobile). The plain web
 * build relies on the backend's HttpOnly cookie (XSS-resistant) and never holds
 * a token in JS. Tauri can't use cookies because the WebView lives at
 * `tauri.localhost` while the backend is at `localhost:3333` (cross-site →
 * `SameSite=Lax` is dropped).
 *
 * Phase 3: the DURABLE copy of the Tauri token moved out of plaintext
 * `localStorage` into the OS keychain (desktop). The hot read-path stays
 * synchronous via an in-memory cache — see `secure-token.ts`.
 */
export { SESSION_TOKEN_STORAGE_KEY };

/**
 * Persists the legacy session token — **Tauri only**, no-op on web. Writes to
 * the native secure store AND the synchronous in-memory cache, and (Phase 2)
 * refreshes the Rust-held active token so the `api_request` proxy injects it.
 * Kept as `writeSessionToken` for its existing callers (auth.ts, login/register,
 * social-callback).
 */
export function writeSessionToken(token: string | null): void {
  writeLegacyToken(token);
}

/**
 * Bearer token the JS layer attaches to API requests — now ALWAYS `null`.
 *
 * Phase 2 ("native HttpOnly"): on native the token never enters JS for the
 * request path — the Rust `api_request` proxy reads it from the secure store and
 * injects `Authorization: Bearer` itself. On web (cookie-BFF) the token likewise
 * never lives in JS (HttpOnly `journal.device` cookie + `X-Active-Account`
 * selector). So JS attaches no Bearer on either platform.
 *
 * Exported for the transport regression tests (native must NOT build a Bearer in
 * JS).
 */
export function getActiveAuthToken(): string | null {
  return null;
}

/**
 * Resolves the secure-store key (userId) whose token Rust should inject on the
 * next API request — **native only**. Prefers THIS tab's active real account and
 * falls back to the legacy single-token slot for back-compat. `null` = guest /
 * unauthenticated → Rust attaches no Bearer. Reads `location.pathname`
 * synchronously (same as the selector), safe to call per request.
 */
function activeTokenAccount(): string | null {
  const selector = readActiveAccountSelector();
  if (selector) {
    return selector;
  }
  // Legacy slot: a cached/plaintext value OR a Rust-held (presence-only) token
  // from the native OAuth deep-link path both count as "authenticated".
  return readLegacyToken() !== null || hasTokenPresence(LEGACY_TOKEN_ACCOUNT)
    ? LEGACY_TOKEN_ACCOUNT
    : null;
}

/**
 * Resolves the active account's userId for the `X-Active-Account` selector
 * header — **web (cookie-BFF) only**.
 *
 *   - Active **real** account → its userId. Combined with the device cookie the
 *     backend resolves THIS tab's session (anti-impersonation: only an account
 *     logged in on this device can be selected).
 *   - Active **guest** account / none → `null` → no header → the device branch
 *     is unauthenticated (clean visitor/local-first, no cross-account leak).
 *   - **Tauri** → `null`: it authenticates directly via Bearer, no selector.
 */
function getActiveAccountSelector(): string | null {
  if (isTauriRuntime()) {
    return null;
  }
  return readActiveAccountSelector();
}
/**
 * Resolved backend origin for this build (e.g. `http://localhost:3333` in
 * Tauri, or the same origin via the Vite proxy on plain web).
 *
 * Exported so non-openapi-fetch transports (multipart upload, plain `<a>`
 * download links) can target the backend with the **same** origin-resolution
 * logic the typed `api` client uses. Without this, relative URLs would resolve
 * against the WebView origin (`tauri.localhost`) and 404 against the SPA's
 * `index.html`.
 */
export const SERVER_BASE_URL = resolveServerUrl({
  isWeb: true,
  isDev: import.meta.env.DEV,
  serverUrl: SERVER_URL,
  location: {
    hostname: globalThis.window.location.hostname,
    protocol: globalThis.window.location.protocol,
    origin: globalThis.window.location.origin,
  },
});

/**
 * Native (Tauri) API transport: every request is routed through the Rust
 * `api_request` command, which injects `Authorization: Bearer` from the secure
 * store — the token never lives in JS (the "native HttpOnly" model, Phase 2).
 * `null` on web, where the client keeps its same-origin cookie transport.
 */
const nativeApiFetch = isTauriRuntime() ? createNativeApiFetch(activeTokenAccount) : null;

export const api = createApiClient({
  baseUrl: SERVER_BASE_URL,
  // Native: hand the client the Rust-proxy transport (Bearer injected in Rust).
  // Web: leave unset → the client uses its built-in cookie-BFF `buildFetch`.
  fetch: nativeApiFetch ?? undefined,
  // Both platforms attach NO Bearer from JS (always `null`); native's header is
  // added by Rust, web relies on the HttpOnly cookie.
  getAuthToken: getActiveAuthToken,
  // Web attaches the `X-Active-Account` selector; Tauri returns `null` (its
  // active account is carried by the Rust-held token instead).
  getActiveAccountId: getActiveAccountSelector,
  // cookie-BFF: the request carries the HttpOnly `journal.device` cookie and the
  // active account is chosen by the `X-Active-Account` header. The token never
  // lives in JS. (On native the custom `fetch` above supersedes this.) OAuth is
  // unaffected (its `state` cookie + callback token ride top-level navigations).
  credentials: "include",
  // Client telemetry. Attached by an openapi-fetch middleware inside the client,
  // so it survives the native `fetch` override above — which is the whole reason
  // it is not in `buildFetch`.
  getClientInfo: buildClientInfoHeader,
  // A 401 only counts as an anomaly when we believed we had an account. On web
  // that is the `X-Active-Account` selector; on native it is the secure-store
  // key Rust injects the Bearer for. Both are already computed above, so this
  // asks the same questions the transport asks and cannot drift from them.
  isAuthenticatedHint: () =>
    (isTauriRuntime() ? activeTokenAccount() : getActiveAccountSelector()) !== null,
  onAuthTransportFailure: recordAuthTransportFailure,
});

/**
 * Fetch for the raw storage transports (multipart upload, blob download) that
 * bypass the typed `api` client. On native it routes through the Rust proxy so
 * these requests ALSO get the Bearer injected out-of-JS; on web it is the plain
 * `fetch` (the device cookie rides `credentials: "include"`).
 */
function platformFetch(input: string | URL | Request, init?: RequestInit): Promise<Response> {
  if (nativeApiFetch) {
    return nativeApiFetch(input, init);
  }
  return fetch(input, init);
}

/**
 * Builds the auth headers for the raw storage transports (multipart upload, blob
 * download) that bypass the typed `api` client. Mirrors the client's auth: web
 * attaches the `X-Active-Account` selector (the device cookie rides
 * `credentials: "include"` at the call site). Native attaches NOTHING here — its
 * Bearer is injected by the Rust proxy (see {@link platformFetch}), so the token
 * never lives in JS.
 */
function buildStorageAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const activeAccountId = getActiveAccountSelector();
  if (activeAccountId) {
    headers["X-Active-Account"] = activeAccountId;
  }
  return headers;
}

/** Shape returned by `POST /api/storage/upload` on both Go and Rust backends. */
interface StorageUploadResult {
  /** Storage key, e.g. `dev/2026/05/30/file.png`. */
  key: string;
  /** Relative or absolute URL to fetch the object back. */
  url: string;
}

/**
 * Uploads a file to the shared object storage (`POST /api/storage/upload`).
 *
 * The endpoint is public (no auth middleware), but we still forward the Tauri
 * bearer token and cookies so it keeps working if auth is added later. We must
 * NOT set `Content-Type` manually — the browser appends the multipart boundary
 * the backend's parser needs.
 */
export async function uploadStorageObject(file: File): Promise<StorageUploadResult> {
  const formData = new FormData();
  formData.append("file", file);

  const headers = buildStorageAuthHeaders();

  const response = await platformFetch(
    `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/storage/upload`,
    {
      method: "POST",
      body: formData,
      credentials: "include",
      headers,
    },
  );

  if (!response.ok) {
    throw new Error(`Storage upload failed with status ${response.status}`);
  }

  // Trust-boundary cast of the server's JSON response; the shape is validated
  // immediately below before any field is used.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const data = (await response.json()) as Partial<StorageUploadResult>;
  if (!data.url) {
    throw new Error("Storage upload response is missing the file URL");
  }
  return { key: data.key ?? "", url: data.url };
}

/** Response of `POST /api/avatar`. */
export interface AvatarUploadResult {
  /** Storage key to write into `user.image`. */
  key: string;
  /** Absolute URL ready for an `<img src>`. */
  url: string;
}

/**
 * Uploads the profile photo (`POST /api/avatar`).
 *
 * Only the BINARY travels: the returned key is written into the local row with
 * `updateUserImageMutation` and syncs on its own via the sync write-path (same
 * as language, font and theme). That way the photo shows instantly and
 * survives offline.
 *
 * Careful: `Content-Type` cannot be set by hand — the browser has to add the
 * multipart boundary the backend's parser expects.
 */
export async function uploadAvatar(file: File): Promise<AvatarUploadResult> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}/api/avatar`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: buildStorageAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Avatar upload failed with status ${response.status}`, {
      cause: response.status,
    });
  }

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const data = (await response.json()) as Partial<AvatarUploadResult>;
  if (!data.key) {
    throw new Error("Avatar upload response is missing the storage key");
  }
  return { key: data.key, url: data.url ?? "" };
}

/** Removes the profile photo (`DELETE /api/avatar`). Falls back to initials. */
export async function deleteAvatar(): Promise<void> {
  const response = await platformFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}/api/avatar`, {
    method: "DELETE",
    credentials: "include",
    headers: buildStorageAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Avatar delete failed with status ${response.status}`, {
      cause: response.status,
    });
  }
}

/**
 * Resolves a stored attachment URL into something this build can open, using
 * the resolved backend origin. Thin wrapper over the shared `buildStorageUrl`
 * so the resolution rules stay identical across web, Tauri and native.
 */
export function resolveStorageUrl(url: string | null | undefined): string {
  return buildStorageUrl(SERVER_BASE_URL, url);
}

type TauriSaveResult = "saved" | "cancelled" | "unavailable";

/**
 * Best-effort native "Save As…" inside Tauri using the dialog + fs plugins.
 *
 * Returns:
 *   - `"saved"`       file written to the user-chosen path
 *   - `"cancelled"`   user dismissed the save dialog (do nothing further)
 *   - `"unavailable"` plugins/scope not usable → caller should fall back
 *
 * The dynamic imports are only reached under `isTauriRuntime()`, so plain web
 * builds never load these chunks. Any failure degrades gracefully to the blob
 * download path; we never block the user on a missing native capability.
 */
async function tryTauriSaveBlob(blob: Blob, filename: string): Promise<TauriSaveResult> {
  try {
    const [{ save }, { writeFile }] = await Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs"),
    ]);
    const path = await save({ defaultPath: filename });
    if (!path) {
      return "cancelled";
    }
    const bytes = new Uint8Array(await blob.arrayBuffer());
    await writeFile(path, bytes);
    return "saved";
  } catch (error) {
    console.warn("[Attachments] native save unavailable, falling back to blob:", error);
    return "unavailable";
  }
}

/** Programmatically triggers a browser download/open for the given href. */
function triggerAnchorDownload(href: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener noreferrer";
  anchor.target = "_blank";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Downloads a stored attachment and hands it to the user as a file.
 *
 * Why not a plain `<a href>`? The production backend protects `/api/storage/*`
 * behind auth. A top-level link/navigation can't attach the Tauri bearer token
 * (cookies are dropped cross-site at `tauri.localhost`), so it would 401 there.
 * Fetching with the same auth transport the API client uses (cookies on web,
 * bearer in Tauri) works identically on every platform; the resulting `blob:`
 * URL is same-origin so opening/saving it needs no further auth.
 */
export async function downloadStorageObject(
  url: string | null | undefined,
  filename: string,
): Promise<void> {
  const resolved = resolveStorageUrl(url);
  if (!resolved) {
    throw new Error("Attachment has no URL to download");
  }

  // Already-local refs (legacy blob:, inline data:) need no fetch.
  if (/^(?:blob:|data:)/iu.test(resolved)) {
    triggerAnchorDownload(resolved, filename);
    return;
  }

  const headers = buildStorageAuthHeaders();

  const response = await platformFetch(resolved, { credentials: "include", headers });
  if (!response.ok) {
    throw new Error(`Attachment download failed with status ${response.status}`);
  }

  const blob = await response.blob();

  // In Tauri the WebView's `<a download>` handling is platform-dependent, so we
  // prefer a real native "Save As…" dialog. If the plugins/scope aren't usable
  // we fall through to the blob anchor below (which still has the fetched bytes).
  if (isTauriRuntime()) {
    const result = await tryTauriSaveBlob(blob, filename);
    if (result === "saved" || result === "cancelled") {
      return;
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerAnchorDownload(objectUrl, filename);
  } finally {
    // Revoke after a delay so the click has consumed the URL first.
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, OBJECT_URL_REVOKE_DELAY_MS);
  }
}

/**
 * Deprecated: Use 'api' instead.
 * Keeping it briefly for compatibility during refactor.
 * @deprecated Use handleApiResponse from @journal/shared directly or the query helpers below.
 */
export async function fetchApi<T>(
  promise: Promise<{ data?: T | null; error?: unknown; response?: Response }>,
): Promise<NonNullable<T>> {
  return handleApiResponse(promise);
}

// ─── Query Helpers for SolidJS ───────────────────────────────────────────────

export function createApiQuery<TData, TError>(
  queryKey: () => QueryKey,
  queryFn: () => Promise<{ data?: TData | null; error?: TError; response?: Response }>,
  options?: () => Omit<
    QueryOptions<NonNullable<TData>, NonNullable<TError>>,
    "queryKey" | "queryFn" | "initialData"
  >,
): UseQueryResult<NonNullable<TData>, NonNullable<TError>> {
  return createQuery(() => ({
    ...options?.(),
    queryKey: queryKey(),
    queryFn: async () => handleApiResponse(queryFn()),
  }));
}

export function createApiMutation<TVar, TData, TError, TContext = unknown>(
  mutationFn: (vars: TVar) => Promise<{ data?: TData | null; error?: TError; response?: Response }>,
  options?: () => Omit<
    MutationOptions<NonNullable<TData>, NonNullable<TError>, TVar, TContext>,
    "mutationFn"
  >,
): UseMutationResult<NonNullable<TData>, NonNullable<TError>, TVar, TContext> {
  return createMutation(() => ({
    ...options?.(),
    mutationFn: async (vars) => handleApiResponse(mutationFn(vars)),
  }));
}
